import { getCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import setShowPricing, { ShowPricingCookieName } from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import { getBillToState, getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getLocation, getPageStateByPath } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pageView, userInformation } from "@WhiteCap/Common/Utillities/CustomTracking";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        pathName: state.data.pages.location.pathname,
        existingPage: getPageStateByPath(state, location.pathname).value,
        userId: state.context.session.userProfileId,
        loginStatus: state.context.session.userProfileId ? "loggedIn" : "loggedOut",
        currentBillTo: getCurrentBillToState(state),
        userSession: state.context.session,
        settings: state.context.settings,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    setShowPricing,
    loadCurrentBillTo,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerWrapper?: InjectableCss;
    mobileSearch?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            display: flex;

            flex-direction: column;

            > div[class^="Wrapper"]:nth-child(2) {
                height: 80px;

                > div[class^="sc"] {
                    height: inherit;
                    background-color: black;
                    align-items: center;
                }

                nav[class^="OverflowWrapper"] {
                    position: unset !important;
                }
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    mobileSearch: {
        css: css`
            margin: 0 auto;
            width: 90%;
            margin-top: 0px;
            padding: 15px 0px;
        `,
    },
    headerWrapper: {
        css: css`
            background-color: black;
            height: auto;
            transition: height 1s;
            top: 0;
            z-index: 10;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 14px;
                    `,
                    css`
                        margin-bottom: 14px;
                    `,
                    css`
                        padding-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
            & + div[class^="PageStyle"] {
                padding: 0px;
                nav[data-test-selector="pageBreadcrumbs"] {
                    width: 100%;
                    display: flex;
                    max-width: 1280px;
                    margin: 0 auto;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                                overflow-x: auto;
                                white-space: nowrap;
                                margin: 0 auto;
                                margin-top: 8px;
                            `,
                    css`
                                overflow-x: auto;
                                white-space: nowrap;
                                margin: 0 auto;
                                margin-top: 8px;
                            `,
                ])}
                    @media only screen and (max-width: 1280px) {
                        padding-left: 15px !important;
                        padding-right: 15px !important;
                    }
                    > span[class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                        font-size: 14px;
                    }
                    span[aria-current="page"] {
                        color: ${getColor("text.accent")};
                        font-weight: bold;
                        font-size: 14px;
                        text-transform: capitalize;
                        opacity: 1;
                    }

                    > a > span[class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                        text-transform: capitalize;
                        opacity: 1;
                        font-size: 14px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                                padding: 0;
                            `,
                    ],
                    "max",
                )}
            }
            ~ main[class^="PageStyle"] {
                min-height: 500px;
                max-width: 1280px;
                > div[class^="LoadingOverlayStyle"]:first-of-type {
                    > div:last-child {
                        > div[class^="GridWrapper"] {
                            max-width: 1280px;
                        }
                    }
                }
                main {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                }

                > div {
                    div[class^="GridWrapper"] {
                        max-width: 1280px;
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                padding: 15px;
                                padding-top: 0px;
                            `,
                        css`
                                padding: 15px;
                                padding-top: 0px;
                            `,
                    ],
                    "max",
                )}
            }
            ~ main[data-test-selector="homePage"] {
                padding: 0;
            }
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    setShowPricing,
    loadCurrentBillTo,
    pathName,
    existingPage,
    userId,
    loginStatus,
    userSession,
    currentBillTo,
    settings,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

   useEffect(() => {
        if (userSession && userSession.isAuthenticated && !currentBillTo?.value) {
            loadCurrentBillTo();
        }
    }, [userSession, currentBillTo]);


    // EC-1433 - Data Layer for user_information
    const firedPagesRef = React.useRef(new Set<string>());
    useEffect(() => {
        if (!userSession) {return;}

        const isLoggedIn = userSession.isAuthenticated;
        const hasAddress = !!currentBillTo?.value?.fullAddress;

        // Prevent multiple fires for the same page view
        if (firedPagesRef.current.has(pathName)) {return;}

        if (!isLoggedIn || hasAddress) {
            userInformation(
                userSession,
                loginStatus,
                hasAddress ? currentBillTo!.value! : null
            );
            firedPagesRef.current.add(pathName);
        }
    }, [userSession, currentBillTo, pathName]);

    useEffect(() => {
        const path = pathName.includes("Catalog") ? pathName.replace("/Catalog/", "/Home/") : `/Home/${pathName}`;
        const values = path.split("/");

        const breadcrumbs = values.map(item => item.replace("-en", "").replace(/-/g, " ")).filter(item => item !== "");
        if (existingPage?.type === "ProductListPage" || existingPage?.type === "CategoryDetailsPage") {
            const pageName = `whitecap | ${breadcrumbs[breadcrumbs.length - 1]}`;
            pageView(breadcrumbs, existingPage?.type, userId, loginStatus, pageName, userSession, currentBillTo);
        } else if (existingPage?.type === "ProductDetailsPage") {
            const sku = breadcrumbs[breadcrumbs.length - 1].split(" ").pop();
            const pageName = `whitecap | ${sku}`;
            pageView(breadcrumbs, existingPage?.type, userId, loginStatus, pageName, userSession, currentBillTo,);
        } else {
            pageView(breadcrumbs, existingPage?.type, userId, loginStatus, window.document.title, userSession, currentBillTo);
        }
    }, [pathName]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        const customBuyerRoleName = settings?.properties.customBuyerRoleName || "";
        if (customBuyerRoleName && session && session?.userRoles?.toLowerCase().includes(customBuyerRoleName.toLowerCase())) {
            const showPricing = getCookie(ShowPricingCookieName)?.toLowerCase() !== "false"
            if (showPricing) {
                setShowPricing({ showPricing: false });
            }
        }
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <Page
            as="header"
            {...styles.page}
            fullWidth={[true, true, true, true, true]}
            {...styles.headerWrapper}
            id="MenuBAr"
            isHeader={true}
        >
            <SkipNav
                extendedStyles={styles.skipToContent}
                text={translate("Skip to main content")}
                destination={afterHeader}
            />
            <EagerLoadingLogoProvider value={true}>
                <Zone contentId={id} zoneName="SecondaryNavigation" />
                <StyledWrapper {...styles.container}>
                    <Zone contentId={id} zoneName="MainNavigation" />
                </StyledWrapper>
                <Hidden above="md" {...styles.mobileSearch}>
                    <Zone contentId={id} zoneName="mobileSearchBar" />
                </Hidden>
            </EagerLoadingLogoProvider>
            <span ref={afterHeader} tabIndex={-1} />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;